// frontend/src/components/forms/EditSubmission.jsx

import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import axios from "axios";
import { getUploadUrls, updateSubmission, deleteFile } from "@/services/apiService";
import MediaPreview from "@/components/ui/MediaPreview";
import { CloseIconButton } from "@/components/ui/button";
import { useModal } from "@/contexts/ModalContext";
import ModalCard from "@/components/ui/ModalCard";
import UploadProgressBar from "@/components/ui/UploadProgressBar";

// DEV-only helper: extract CSRF token from cookies
function getCsrfToken() {
  const name = "csrftoken";
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}

const EditSubmission = () => {
  const { modalType, modalProps, closeModal } = useModal();

  const isOpen = modalType === "EDIT_SUBMISSION";
  const submission = modalProps?.submission || null;
  const updateSubmissions = modalProps?.updateSubmissions || null;

  const submissionId = submission?.id ?? null;

  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);       // existing backend files
  const [newFiles, setNewFiles] = useState([]);  // File objects
  const [blobUrls, setBlobUrls] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // ── Upload progress ──────────────────────────────────────────
  const [fileProgress, setFileProgress] = useState({});

  const uploadProgress = (() => {
    const values = Object.values(fileProgress);
    if (values.length === 0) return 0;
    return values.reduce((sum, v) => sum + v, 0) / values.length;
  })();
  // ────────────────────────────────────────────────────────────

  const isDescriptionInvalid = errorMessage === "You forgot the description!";

  useEffect(() => {
    if (!isOpen || !submission) return;
    setDescription(submission.description || "");
    setFiles(submission.files || []);
    setNewFiles([]);
    setBlobUrls({});
    setErrorMessage(null);
    setSuccessMessage(null);
    setFileProgress({});
  }, [isOpen, submission]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      Object.values(blobUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    const newUrls = {};
    selectedFiles.forEach((f) => { newUrls[f.name] = URL.createObjectURL(f); });
    setNewFiles((prev) => [...prev, ...selectedFiles]);
    setBlobUrls((prev) => ({ ...prev, ...newUrls }));
  };

  const removeFile = async (fileIndex, isNew = false) => {
    if (isNew) {
      const fileToRemove = newFiles[fileIndex];
      if (fileToRemove && blobUrls[fileToRemove.name]) {
        URL.revokeObjectURL(blobUrls[fileToRemove.name]);
        setBlobUrls((prev) => {
          const next = { ...prev };
          delete next[fileToRemove.name];
          return next;
        });
      }
      setNewFiles((prev) => prev.filter((_, index) => index !== fileIndex));
      return;
    }

    const fileToRemove = files[fileIndex];
    setFiles((prev) => prev.filter((_, index) => index !== fileIndex));

    try {
      if (fileToRemove?.file_url) {
        await deleteFile(fileToRemove.file_url);
      }
    } catch (err) {
      console.error("Failed to delete file from storage:", err);
    }
  };

  const handleSubmit = async () => {
    setErrorMessage(null);
    setSuccessMessage(null);
    setFileProgress({});

    if ([...files, ...newFiles].length === 0) {
      setErrorMessage("You forgot to add your photos and videos!");
      return;
    }
    if (!description.trim()) {
      setErrorMessage("You forgot the description!");
      return;
    }
    if (!submissionId) {
      setErrorMessage("Missing submission id.");
      return;
    }

    setIsUploading(true);

    // Initialise progress for each new file
    if (newFiles.length > 0) {
      const initialProgress = {};
      newFiles.forEach((_, i) => { initialProgress[i] = 0; });
      setFileProgress(initialProgress);
    }

    const uploadedFiles = [];

    try {
      if (newFiles.length > 0) {
        const urlPairs = await Promise.all(newFiles.map((file) => getUploadUrls(file)));

        const uploadPromises = newFiles.map((file, index) => {
          const { upload_url, file_url } = urlPairs[index];

          uploadedFiles.push({
            file_url,
            file_type: file.type.split("/")[0],
          });

          const headers = { "Content-Type": file.type };
          if (import.meta.env.DEV) {
            const csrf = getCsrfToken();
            if (csrf) headers["X-CSRFToken"] = csrf;
          }

          return axios.put(upload_url, file, {
            headers,
            withCredentials: Boolean(import.meta.env.DEV),
            // ── Progress callback ──────────────────────────────
            onUploadProgress: (event) => {
              if (event.total) {
                const pct = Math.round((event.loaded / event.total) * 100);
                setFileProgress((prev) => ({ ...prev, [index]: pct }));
              }
            },
            // ──────────────────────────────────────────────────
          });
        });

        await Promise.all(uploadPromises);

        // Mark all at 100%
        setFileProgress((prev) => {
          const complete = { ...prev };
          newFiles.forEach((_, i) => { complete[i] = 100; });
          return complete;
        });
      }

      // Build canonical files_metadata for DB
      const files_metadata = [
        ...files.map((f) => ({ file_url: f.file_url, file_type: f.file_type })),
        ...uploadedFiles,
      ];

      const updatedSubmission = await updateSubmission(submissionId, {
        description,
        files_metadata,
      });

      if (typeof updateSubmissions === "function") {
        updateSubmissions(updatedSubmission);
      }

      setSuccessMessage(true);
      setFileProgress({});
    } catch (error) {
      let msg = "An error occurred while updating the submission.";

      if (error?.response?.data) {
        if (error.response.data.error) {
          msg = error.response.data.error;
        } else if (typeof error.response.data === "object") {
          const firstError = Object.values(error.response.data)[0];
          if (Array.isArray(firstError)) msg = firstError[0];
          else if (typeof firstError === "string") msg = firstError;
        }
      } else if (error?.message) {
        msg = error.message;
      }

      setErrorMessage(msg);
      setFileProgress({});

      // Best-effort cleanup
      try {
        if (uploadedFiles.length > 0) {
          await Promise.all(uploadedFiles.map((f) => deleteFile(f.file_url)));
        }
      } catch {
        // ignore
      }
    } finally {
      setIsUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) closeModal(); }}>
      <DialogContent className="flex justify-center items-center">
        <ModalCard title="Edit Submission" onClose={() => closeModal()}>
          <div className="space-y-3 py-1">
            {/* Description */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-swan">Description</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className={`w-full h-24 border-collapse bg-transparent border-dark-grey text-field-grey ${
                  isDescriptionInvalid ? "border border-imperial-red" : ""
                }`}
              />
            </div>

            {/* File Manager */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-swan">Manage Files</label>
              <div className="border-2 border-dashed border-dark-grey rounded-lg p-10">
                <div className="flex flex-wrap gap-2 justify-center">
                  {[...files, ...newFiles].map((file, index) => {
                    const isNew = !file?.file_url;
                    const previewFile = isNew
                      ? {
                          file_url: blobUrls[file.name] || URL.createObjectURL(file),
                          file_type: file.type ? file.type.split("/")[0] : "",
                        }
                      : file;

                    return (
                      <div
                        key={index}
                        className="relative w-24 aspect-[1/1] flex items-center justify-center bg-black rounded-lg overflow-hidden"
                      >
                        <MediaPreview file={previewFile} />
                        <CloseIconButton
                          onClick={() => removeFile(index, isNew)}
                          className="absolute top-1 right-1"
                        />
                      </div>
                    );
                  })}
                </div>

                <div className="flex justify-center mt-4">
                  <input
                    type="file"
                    multiple
                    accept="image/*,video/*"
                    onChange={handleFileInput}
                    className="hidden"
                    id="file-input"
                  />
                  <Button
                    variant="primary"
                    onClick={() => document.getElementById("file-input").click()}
                    disabled={isUploading}
                  >
                    Upload Files
                  </Button>
                </div>
              </div>
            </div>

            {/* ── Upload progress bar ── */}
            {isUploading && newFiles.length > 0 && (
              <UploadProgressBar progress={uploadProgress} />
            )}
            {/* ─────────────────────── */}

            <div className="space-y-1">
              {errorMessage && (
                <p className="mt-2 font-semibold text-imperial-red">{errorMessage}</p>
              )}
              {successMessage && (
                <p className="text-sm text-mikado-yellow animate-pulse">
                  Updated Successfully!
                </p>
              )}
            </div>

            <div>
              <Button
                onClick={handleSubmit}
                variant="primary"
                className="w-full"
                disabled={isUploading}
              >
                {isUploading ? "Saving…" : "Save Changes"}
              </Button>
            </div>
          </div>
        </ModalCard>
      </DialogContent>
    </Dialog>
  );
};

export default EditSubmission;